import { AlertTriangle, CalendarClock, PiggyBank, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useI18n } from '@/hooks/useI18n';
import { formatPlanAmount } from '@/lib/builder/utils/payment-format';
import { DUE_WINDOW_ALL, IPaymentsKpis } from '@/lib/payments/models/payments-dashboard.model';
import { payerKey } from '@/lib/payments/utils/traveler-copy';

interface PaymentsKpiRowProps {
    kpis: IPaymentsKpis;
    dueWindowDays: number;
    /** Il viaggiatore paga: "da incassare"/"incassato" diventano "da pagare"/"pagato". */
    travelerView?: boolean;
}

interface KpiTileProps {
    label: string;
    value: string;
    hint?: string;
    icon: typeof Wallet;
    tone: 'neutral' | 'danger' | 'warning' | 'success';
}

const TONES: Record<KpiTileProps['tone'], { icon: string; value: string }> = {
    neutral: { icon: 'bg-gray-100 text-gray-600', value: 'text-gray-900' },
    danger: { icon: 'bg-red-100 text-red-600', value: 'text-red-700' },
    warning: { icon: 'bg-amber-100 text-amber-600', value: 'text-amber-700' },
    success: { icon: 'bg-green-100 text-green-600', value: 'text-green-700' },
};

const KpiTile = ({ label, value, hint, icon: Icon, tone }: KpiTileProps) => {
    const tones = TONES[tone];

    return (
        <Card className="p-4 flex items-start gap-3">
            <span className={`rounded-lg p-2 flex-shrink-0 ${tones.icon}`}>
                <Icon className="w-4 h-4" />
            </span>
            <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                    {label}
                </span>
                <span className={`text-xl font-semibold tabular-nums truncate ${tones.value}`}>
                    {value}
                </span>
                {hint && <span className="text-xs text-gray-500 mt-0.5">{hint}</span>}
            </div>
        </Card>
    );
};

/**
 * I quattro numeri in cima alla pagina.
 *
 * Sono calcolati dalla RPC sullo stesso insieme filtrato delle liste sotto: cambiare
 * collaboratore o ricerca li muove insieme alle tabelle. Se fossero calcolati qui sui
 * dati gia' scaricati direbbero un'altra cosa, perche' le liste sono troncate.
 */
export const PaymentsKpiRow = ({ kpis, dueWindowDays, travelerView }: PaymentsKpiRowProps) => {
    const { t } = useI18n();

    // Da xl in su i KPI stanno in una colonna larga 7/10, non piu' a tutta pagina: quattro
    // tile affiancate li' schiaccerebbero gli importi. Restano 2x2 fino a 2xl, dove la
    // colonna e' abbastanza larga da tenerne quattro.
    return (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4">
            <KpiTile
                icon={AlertTriangle}
                tone={kpis.overdue_count > 0 ? 'danger' : 'neutral'}
                label={t('payments.kpi.overdue')}
                value={formatPlanAmount(kpis.overdue_amount)}
                hint={t('payments.kpi.installmentsCount', { count: kpis.overdue_count })}
            />
            <KpiTile
                icon={CalendarClock}
                tone={kpis.due_soon_count > 0 ? 'warning' : 'neutral'}
                label={
                    dueWindowDays === DUE_WINDOW_ALL
                        ? t('payments.kpi.dueSoonAll')
                        : t('payments.kpi.dueSoon', { days: dueWindowDays })
                }
                value={formatPlanAmount(kpis.due_soon_amount)}
                hint={t('payments.kpi.installmentsCount', { count: kpis.due_soon_count })}
            />
            <KpiTile
                icon={Wallet}
                tone="neutral"
                label={t(payerKey('payments.kpi.remaining', travelerView))}
                value={formatPlanAmount(kpis.remaining_amount)}
                hint={t('payments.kpi.overTotal', { total: formatPlanAmount(kpis.total_amount) })}
            />
            <KpiTile
                icon={PiggyBank}
                tone="success"
                label={t(payerKey('payments.kpi.collected', travelerView))}
                value={formatPlanAmount(kpis.paid_amount)}
                hint={t('payments.kpi.tripsCount', { count: kpis.itineraries_count })}
            />
        </div>
    );
};
